import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Button,
	TextField,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	AI_MODEL_FILE_LOADING_KEY,
	updateAIModelFile,
	UpdateAIModelFileValidationSchema,
	IUpdateAIModelFileFormFields,
	IAIModelFileDetails,
} from 'src/features/ai_model_file';
import { AppDispatch, RootState } from 'src/store';

interface IUpdateAIModelFileModalProps {
	initialValues: IUpdateAIModelFileFormFields;
	isOpen: boolean;
	fileId: number;
	onClose: () => void;
	onSuccess: (updated: IAIModelFileDetails) => void;
}

const UpdateAIModelFileModal: React.FC<IUpdateAIModelFileModalProps> = ({
	isOpen,
	fileId,
	onClose,
	onSuccess,
	initialValues,
}) => {
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[AI_MODEL_FILE_LOADING_KEY],
	);

	const {
		control,
		reset,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm<IUpdateAIModelFileFormFields>({
		resolver: yupResolver(UpdateAIModelFileValidationSchema),
		defaultValues: {
			name: initialValues.name || '',
			description: initialValues.description || '',
		},
	});

	const handleSubmitClick = async (data: IUpdateAIModelFileFormFields) => {
		if (!siteSlug || !fileId) return;

		dispatch(
			updateAIModelFile({
				siteSlug,
				fileId,
				request: {
					name: data.name,
					description: data.description,
				},
				onSuccess: (updated) => {
					snackbar('File updated successfully', 'success');
					onSuccess(updated);
					handleDialogClose();
				},
			}),
		);
	};

	const handleDialogClose = () => {
		if (isLoading) return;
		reset();
		onClose();
	};

	useEffect(() => {
		if (!isOpen) return;
		reset({
			name: initialValues.name || '',
			description: initialValues.description || '',
		});
	}, [isOpen, initialValues.name, initialValues.description, reset]);

	if (!isOpen) return null;

	return (
		<Dialog
			open={isOpen}
			PaperProps={{ sx: { width: 500 } }}
			onClose={(event, reason) => {
				if (
					isLoading &&
					(reason === 'backdropClick' || reason === 'escapeKeyDown')
				)
					return;
				handleDialogClose();
			}}
			disableEscapeKeyDown={isLoading}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					pb: 0,
					mt: 1,
				}}
			>
				<Typography variant='h6' sx={{ fontWeight: 700 }}>
					Edit AI Model File
				</Typography>
				<IconButton onClick={handleDialogClose} disabled={isLoading}>
					<CloseIcon fontSize='small' />
				</IconButton>
			</DialogTitle>

			<DialogContent>
				<form
					onSubmit={handleSubmit(handleSubmitClick)}
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '1.5rem',
						paddingTop: '1rem',
					}}
				>
					<Controller
						name='name'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Name'
								variant='outlined'
								fullWidth
								error={!!errors.name}
								helperText={errors.name?.message}
								disabled={isLoading}
							/>
						)}
					/>

					<Controller
						name='description'
						control={control}
						render={({ field }) => (
							<TextField
								{...field}
								label='Description'
								variant='outlined'
								fullWidth
								multiline
								rows={3}
								error={!!errors.description}
								helperText={errors.description?.message}
								disabled={isLoading}
							/>
						)}
					/>

					<Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
						<PrimaryButton
							type='submit'
							variant='contained'
							fullWidth
							disabled={isLoading || !isDirty}
							isLoading={isLoading}
						>
							UPDATE
						</PrimaryButton>
						<Button
							variant='outlined'
							color='error'
							fullWidth
							onClick={handleDialogClose}
							disabled={isLoading}
						>
							CANCEL
						</Button>
					</Box>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default UpdateAIModelFileModal;
